import { URLS } from '../../../consts/endpoints';
import RESPONSE_CODES from '../../../consts/responseCodes';
import { getData } from '../../../utils/http';
import {
  isArray,
  isDefined,
  isObject,
  isUndefined
} from '../../../utils/isDefined';
import Optional from '../../../utils/optional';

const SUBS_VALUES = {
  action: 'put_on_hold',
  allow_outporting: false,
  termination_date: null,
  after_termination_period: false
};

export const splitSimCards = (simCards = []) => ({
  datacards: simCards.filter(card => card.data_card === true),
  standardCards: simCards.filter(
    card => card.data_card !== true && card.card_type !== 'data_sharing_card'
  )
});

export const createSubscriptionValues = (action = SUBS_VALUES.action) => ({
  ...SUBS_VALUES,
  action
});

export const addMissingNumber = (error, msisdn) => ({
  ...error,
  numbers: [...(error.numbers || []), msisdn]
});

export const mapUser = data => {
  if (!isObject(data)) {
    return {};
  }

  const { name, user_id, email, phone_numbers, user_profile } = data;

  return {
    name,
    user_id,
    email,
    user_profile,
    ...mapNumbers(phone_numbers)
  };
};

export const getSubscription = async (msisdn, clientId) => {
  const { data } = await getData({
    options: {
      url: `${URLS.QUOTE_CLIENTS}/${clientId}/subscriptions/search?phoneNumber[]=${msisdn}&execute_fetch_events=false&return_projections=false`
    },
    errorsToPass: [
      RESPONSE_CODES.CONNECTION_TIMEOUT,
      RESPONSE_CODES.INTERNAL_ERROR,
      RESPONSE_CODES.UNAUTHORIZED
    ]
  });

  return Optional(data?.[msisdn]?.[0]).or(void 0);
};

export const mapStandardCards = async (cards, clientId) => {
  const results = await Promise.all(
    cards.map(async card => {
      const { msisdn } = card;
      if (isUndefined(msisdn)) {
        return { card, subscriptionValues: {}, missingNumber: void 0 };
      }

      const subscription = await getSubscription(msisdn, clientId);
      let subDetails = isObject(subscription) ? { ...subscription } : {};

      if (isDefined(subscription)) {
        const detailsResponse = await getData(
          `${URLS.QUOTE_CLIENTS_V2}/${clientId}/subscriptions/${subscription.externalReference}`
        );

        if (isDefined(detailsResponse)) {
          subDetails = detailsResponse;
        }
      }

      return {
        card: {
          ...card,
          sub: isDefined(subscription)
            ? { ...subscription, subDetails }
            : void 0
        },
        subscriptionValues: isDefined(subscription)
          ? { [subscription.id]: createSubscriptionValues() }
          : {},
        missingNumber: isUndefined(subscription) ? msisdn : void 0
      };
    })
  );

  return mapCardResults(results);
};

export const mapDatacards = async (cards, clientId) => {
  const results = await Promise.all(
    cards.map(async card => {
      const { msisdn } = card;
      const subscription = await getSubscription(msisdn, clientId);

      return {
        card: { ...card, sub: subscription },
        subscriptionValues: isDefined(subscription)
          ? { [subscription.id]: createSubscriptionValues('terminate') }
          : {},
        missingNumber: isUndefined(subscription) ? msisdn : undefined
      };
    })
  );

  return mapCardResults(results);
};

const mapCardResults = results =>
  results.reduce(
    (collected, result) => ({
      cards: [...collected.cards, result.card],
      values: { ...collected.values, ...result.subscriptionValues },
      error: isDefined(result.missingNumber)
        ? addMissingNumber(collected.error, result.missingNumber)
        : collected.error
    }),
    { cards: [], values: {}, error: {} }
  );

const mapNumbers = numbers => {
  if (!isArray(numbers)) {
    return {};
  }

  return {
    fixed: numbers
      .filter(number => number.number_type === 'OFFICE_FIXED')
      .map(number => number.value),
    mobile: numbers
      .filter(number => number.number_type === 'OFFICE_MOBILE')
      .map(number => number.value)
  };
};
